import { Close } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useSubmitPartnershipInquiryMutation } from "features/partners/partnersApi";
import type { Partner, PartnerType, IndustryType } from "types/partners";
import type { User } from "types/interfaces/user";
import { CALIBRATE_FONT_FAMILY_ACCENT } from "components/hoc/Theme";
import CustomScrollbars from "components/CustomScrollbars";
import {
  AutocompleteScrollListbox,
  autocompleteScrollPaperProps,
} from "components/common/AutocompleteScrollListbox";
import FieldLabel from "components/common/FieldLabel";

interface PartnershipInquiryModalProps {
  open: boolean;
  onClose: () => void;
  partners: Partner[];
  preselectedPartnerSlug?: string;
}

interface FormData {
  provider: Partner | null;
  providerNameOther: string;
  name: string;
  phone: string;
  email: string;
  message: string;
}

export default function PartnershipInquiryModal({
  open,
  onClose,
  partners,
  preselectedPartnerSlug,
}: PartnershipInquiryModalProps) {
  const currentUser = useSelector(
    (state: { auth: { user: User | null } }) => state.auth.user,
  );
  const [submitInquiry, { isLoading }] = useSubmitPartnershipInquiryMutation();

  const [formData, setFormData] = useState<FormData>({
    provider: preselectedPartnerSlug
      ? partners.find((p) => p.slug === preselectedPartnerSlug) || null
      : null,
    providerNameOther: "",
    name: "",
    phone: "",
    email: currentUser?.email || "",
    message: "",
  });

  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>(
    {},
  );
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (open) {
      setFormData({
        provider: preselectedPartnerSlug
          ? partners.find((p) => p.slug === preselectedPartnerSlug) || null
          : null,
        providerNameOther: "",
        name: "",
        phone: "",
        email: currentUser?.email || "",
        message: "",
      });
      setErrors({});
      setSubmitted(false);
    }
  }, [open, preselectedPartnerSlug, currentUser?.email, partners]);

  const isOtherSelected = formData.provider?.slug === "other";

  const partnerOptions = useMemo(() => {
    return [
      ...partners,
      {
        id: -1,
        slug: "other",
        name: "Other",
        partner_type: "service" as PartnerType,
        partner_type_display: "",
        industry_type: "payroll_hcm" as IndustryType,
        industry_type_display: "",
        tile_description: "",
        is_featured: false,
        logo_url: "",
      } as unknown as Partner,
    ];
  }, [partners]);

  const formatPhoneNumber = (value: string): string => {
    const digits = value.replace(/\D/g, "");
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof FormData, string>> = {};

    if (!formData.provider) {
      newErrors.provider = "Please select a provider";
    }

    if (isOtherSelected && !formData.providerNameOther.trim()) {
      newErrors.providerNameOther = "Please enter the provider name";
    }

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    const hasPhone = !!formData.phone.trim();
    const hasEmail = !!formData.email.trim();

    if (!hasPhone && !hasEmail) {
      newErrors.phone = "Provide an email or phone number";
      newErrors.email = "Provide an email or phone number";
    } else {
      if (hasPhone) {
        const digits = formData.phone.replace(/\D/g, "");
        if (digits.length < 10) {
          newErrors.phone = "Please enter a valid phone number";
        }
      }

      if (hasEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = "Please enter a valid email";
      }
    }

    if (!formData.message.trim()) {
      newErrors.message = "Please let us know what we can help you with";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      await submitInquiry({
        partner_slug: isOtherSelected ? undefined : formData.provider?.slug,
        partner_name_other: isOtherSelected
          ? formData.providerNameOther
          : undefined,
        user_name: formData.name,
        user_phone: formData.phone,
        user_email: formData.email,
        message: formData.message,
      }).unwrap();

      setSubmitted(true);
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (error) {
      console.error("Failed to submit inquiry:", error);
    }
  };

  const isSubmitDisabled =
    isLoading ||
    !formData.provider ||
    !formData.name ||
    !(formData.email || formData.phone) ||
    !formData.message ||
    (isOtherSelected && !formData.providerNameOther);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          width: "540px",
          maxWidth: "90vw",
          m: 2,
        },
      }}
    >
      <DialogTitle
        sx={{
          fontFamily: CALIBRATE_FONT_FAMILY_ACCENT,
          backgroundColor: "#185059",
          color: "#FFFFFF",
          textAlign: "center",
          py: "24px",
          position: "relative",
        }}
      >
        Contact the Partnerships Team
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "rgba(255,255,255,0.7)",
            "&:hover": {
              color: "#FFFFFF",
            },
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, display: "flex", overflow: "hidden" }}>
        <CustomScrollbars
          sx={{ width: "100%" }}
          trackOffsetTop={8}
          trackOffsetBottom={8}
        >
          <Box
            sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}
          >
            {submitted ? (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: CALIBRATE_FONT_FAMILY_ACCENT,
                    color: "success.main",
                    mb: 2,
                  }}
                >
                  Thank you!
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ fontFamily: CALIBRATE_FONT_FAMILY_ACCENT }}
                  color="text.secondary"
                >
                  Your inquiry has been submitted. Our partnerships team will reach
                  out to you soon.
                </Typography>
              </Box>
            ) : (
              <>
                <Typography
                  variant="body1"
                  sx={{
                    fontFamily: CALIBRATE_FONT_FAMILY_ACCENT,
                    textAlign: "center",
                    color: "text.secondary",
                    mt: 1,
                    mb: 1.5,
                    pb: 2,
                    borderBottom: "1px solid #CAC4D0",
                  }}
                >
                  Fill in the details below and a member of the CalibrateHCM
                  Partnerships Team will reach out to assist you.
                </Typography>

                <Box>
                  <FieldLabel required>
                    What provider are you interested in?
                  </FieldLabel>
                  <Autocomplete
                    fullWidth
                    options={partnerOptions}
                    value={formData.provider}
                    onChange={(_e, v) => setFormData({ ...formData, provider: v })}
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(option, value) =>
                      option.slug === value.slug
                    }
                    componentsProps={autocompleteScrollPaperProps}
                    ListboxComponent={AutocompleteScrollListbox}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select a provider"
                        error={!!errors.provider}
                        helperText={
                          errors.provider ||
                          (!isOtherSelected
                            ? "Select the provider you would like to learn more about. Don't see a provider you're looking for? Choose Other and let us know who you'd like to work with!"
                            : "")
                        }
                        sx={{
                          "& .MuiFormHelperText-root": {
                            fontFamily: CALIBRATE_FONT_FAMILY_ACCENT,
                          },
                        }}
                      />
                    )}
                  />
                </Box>

                {isOtherSelected && (
                  <Box>
                    <FieldLabel required>Provider Name</FieldLabel>
                    <TextField
                      fullWidth
                      value={formData.providerNameOther}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          providerNameOther: e.target.value,
                        })
                      }
                      error={!!errors.providerNameOther}
                      helperText={errors.providerNameOther}
                      placeholder="Enter the provider name"
                      sx={{
                        "& .MuiFormHelperText-root": {
                          fontFamily: CALIBRATE_FONT_FAMILY_ACCENT,
                        },
                      }}
                    />
                  </Box>
                )}

                <Box>
                  <FieldLabel required>Name</FieldLabel>
                  <TextField
                    fullWidth
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    error={!!errors.name}
                    helperText={errors.name}
                    placeholder="Enter your name"
                    sx={{
                      "& .MuiFormHelperText-root": {
                        fontFamily: CALIBRATE_FONT_FAMILY_ACCENT,
                      },
                    }}
                  />
                </Box>

                <Box>
                  <FieldLabel required>Contact Information</FieldLabel>
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: CALIBRATE_FONT_FAMILY_ACCENT,
                      color: "text.secondary",
                      mt: -0.25,
                      mb: 1,
                    }}
                  >
                    Please include either a phone number or email address
                  </Typography>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                    <TextField
                      fullWidth
                      value={formData.phone}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          phone: formatPhoneNumber(e.target.value),
                        })
                      }
                      error={!!errors.phone}
                      helperText={errors.phone}
                      placeholder="Phone Number"
                      sx={{
                        "& .MuiFormHelperText-root": {
                          fontFamily: CALIBRATE_FONT_FAMILY_ACCENT,
                        },
                      }}
                    />
                    <TextField
                      fullWidth
                      value={formData.email}
                      onChange={(e) =>
                        setFormData({ ...formData, email: e.target.value })
                      }
                      error={!!errors.email}
                      helperText={
                        errors.email || (
                          <Typography
                            component="span"
                            variant="caption"
                            sx={{
                              fontFamily: CALIBRATE_FONT_FAMILY_ACCENT,
                              color: "#31819F",
                              fontWeight: 600,
                            }}
                          >
                            Auto-populated from your user profile.
                          </Typography>
                        )
                      }
                      placeholder="Email"
                      sx={{
                        "& .MuiFormHelperText-root": {
                          fontFamily: CALIBRATE_FONT_FAMILY_ACCENT,
                        },
                      }}
                    />
                  </Box>
                </Box>

                <Box>
                  <FieldLabel required>What can we help you with?</FieldLabel>
                  <TextField
                    fullWidth
                    value={formData.message}
                    onChange={(e) =>
                      setFormData({ ...formData, message: e.target.value })
                    }
                    error={!!errors.message}
                    helperText={errors.message}
                    placeholder="Tell us what you're hoping to accomplish with this partner"
                    multiline
                    rows={3}
                    sx={{
                      "& .MuiFormHelperText-root": {
                        fontFamily: CALIBRATE_FONT_FAMILY_ACCENT,
                      },
                    }}
                  />
                </Box>

                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={isSubmitDisabled || isLoading}
                  sx={{
                    fontFamily: CALIBRATE_FONT_FAMILY_ACCENT,
                    mt: 1,
                    backgroundColor: "#185059",
                    color: "#FFFFFF",
                    textTransform: "uppercase",
                    fontWeight: 500,
                    py: 1.5,
                    boxShadow: 2,
                    "&:hover": {
                      backgroundColor: "#257885",
                      boxShadow: "0 4px 8px rgba(0,0,0,0.25)",
                    },
                    "&:active": {
                      backgroundColor: "#185059",
                      boxShadow: "0 1px 2px rgba(0,0,0,0.25)",
                    },
                    "&:disabled": {
                      backgroundColor: "rgba(24, 80, 89, 0.5)",
                      color: "rgba(255,255,255,0.7)",
                    },
                  }}
                >
                  {isLoading ? "Submitting..." : "Submit Request"}
                </Button>
              </>
            )}
          </Box>
        </CustomScrollbars>
      </DialogContent>
    </Dialog>
  );
}
